import { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, getComicsByCategory } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

const categoryNameMap: Record<string, string> = {
  romance: '恋爱', fantasy: '奇幻', action: '动作', comedy: '搞笑',
  thriller: '悬疑', drama: '剧情', daily: '日常', school: '校园', workplace: '职场',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');
  const [comics, setComics] = useState<Comic[]>([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  useEffect(() => {
    setComics(getComicsByCategory(activeCategory));
  }, [activeCategory]);

  const currentCategoryName = useMemo(() => {
    const cat = categories.find((c) => c.slug === activeCategory);
    return cat ? cat.name : '全部';
  }, [activeCategory]);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-20 md:pt-24">
        <section className="py-8 md:py-10 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-6 md:px-10">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6">
              <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700">分类</span>
              {activeCategory !== 'all' && (
                <>
                  <i className="ri-arrow-right-s-line text-xs"></i>
                  <span className="text-foreground-900 font-medium">{currentCategoryName}</span>
                </>
              )}
            </nav>

            <h1 className="text-2xl md:text-3xl font-display font-bold text-foreground-900 mb-2">
              {currentCategoryName}韩国漫画
            </h1>
            <p className="text-sm text-foreground-500 mb-8">
              浏览{currentCategoryName}分类下的无删减韩国漫画，享受下拉式免费阅读体验
            </p>

            <div className="flex items-center gap-2 flex-wrap mb-10">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {comics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                {comics.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/comic/${comic.id}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200 hover:border-primary-300 transition-all duration-300"
                  >
                    <div className="relative aspect-[3/4] overflow-hidden">
                      <img
                        src={comic.cover}
                        alt={comic.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      {comic.status === '连载中' && (
                        <span className="absolute top-2 left-2 bg-accent-500 text-background-50 text-xs px-2 py-0.5 rounded-full font-medium">
                          {comic.status}
                        </span>
                      )}
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3 pt-10">
                        <div className="flex items-center gap-1 text-background-50 text-xs">
                          <i className="ri-star-fill text-yellow-400 text-xs"></i>
                          <span>{comic.rating}</span>
                          <span className="mx-1">·</span>
                          <span>{comic.views >= 10000 ? `${(comic.views / 10000).toFixed(0)}万` : comic.views}阅读</span>
                        </div>
                      </div>
                    </div>
                    <div className="p-3 md:p-4">
                      <h3 className="text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                        {comic.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        {comic.categories.slice(0, 2).map((cat) => (
                          <span key={cat} className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700">
                            {categoryNameMap[cat] || cat}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无漫画，请查看其他分类</p>
                <Link to="/category/all" className="inline-block mt-4 text-primary-500 hover:text-primary-600 font-medium">
                  查看全部漫画
                </Link>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${currentCategoryName}韩国漫画 - 韩漫大全`,
            description: `浏览${currentCategoryName}分类下的无删减韩国漫画，享受下拉式免费阅读体验`,
            url: `https://www.hanmandq.com/category/${activeCategory}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '韩漫大全',
              url: 'https://www.hanmandq.com/',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.hanmandq.com/' },
              { '@type': 'ListItem', position: 2, name: currentCategoryName, item: `https://www.hanmandq.com/category/${activeCategory}` },
            ],
          }),
        }}
      />
    </div>
  );
}